import React from "react";
import dayjs from "dayjs";
import {
  OverviewContainer,
  BasicInformation,
  BasicInformationContent,
  BasicInformationContentAvatar,
  BasicInformationContentData,
  BasicInformationHeader,
  BasicInformationContentDataItem,
  InfoItem,
  InfoItemRow,
  LoginInformation,
  LoginInformationContent,
  LoginInformationContentCardWrapper,
  LoginInformationContentCardLeft,
  LoginInformationContentCardRight,
  MultiFactor,
  MultiFactorHeader,
  MultiFactorState,
  MultiFactorStateLeft,
  MultiFactorStateRight,
  BasicInformationNotifications,
  BasicInformationNotificationsInvExpired,
  BasicInformationNotificationsInvSent
} from "./Overview.styled";
import { Typography, Button, Avatar, Tag, Icon, Card, IconHighlight, Alert, CircularProgress } from "@ann/web-components";
import { formatLastLogin } from "../../../../utils";

// Format creation date: "Jan 15 · 2024"
const formatCreationDate = (date) => {
  if (!date) return "—";
  try {
    return dayjs(date).format("MMM D · YYYY");
  } catch (error) {
    console.error("Error formatting creation date:", error);
    return "—";
  }
};

// Map user_type to display format
const getUserTypeDisplay = (userType) => {
  if (!userType) return "—";
  // Map common types
  const typeMap = {
    superadmin: "Admin",
    admin: "Admin",
    standard: "Standard",
    STANDARD: "Standard",
    SUPERADMIN: "Admin",
    ADMIN: "Admin",
  };
  return typeMap[userType.toLowerCase()] || userType;
};

// Map state to Tag color
const getStateTagColor = (state) => {
  const normalizedState = typeof state === 'string' ? state.toUpperCase() : '';
  switch (normalizedState) {
    case 'ACTIVE':
      return 'success';
    case 'PENDING':
      return 'alert';
    case 'INACTIVE':
      return 'neutral';
    case 'EXPIRED':
      return 'error';
    default:
      return 'neutral';
  }
};

// Map state to display format
const getStateDisplay = (state) => {
  if (!state) return "—";
  return state.charAt(0).toUpperCase() + state.slice(1).toLowerCase();
};

const Overview = ({ userData, loading, error }) => {
  // Determine which notifications to show based on user state
  const showNotificationDisabled = userData?.state === "INACTIVE" || userData?.deactivatedDate !== null;
  const showInvitationExpired = userData?.state === "EXPIRED";
  const showInvitationSent = userData?.state === "PENDING";

  // Show loading state
  if (loading) {
    return (
      <OverviewContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px' }}>
          <CircularProgress />
        </div>
      </OverviewContainer>
    );
  }

  // Show error state
  if (error) {
    return (
      <OverviewContainer>
        <Alert
          severity="error"
          title="Error loading user data"
          description={error.message || "Failed to load user information. Please try again."}
          closable={false}
        />
      </OverviewContainer>
    );
  }

  // Show empty state if no data
  if (!userData) {
    return (
      <OverviewContainer>
        <Alert
          severity="neutral"
          title="No user data available"
          description="User information could not be found."
          closable={false}
        />
      </OverviewContainer>
    );
  }

  return (
    <OverviewContainer>
      <BasicInformation>
        <BasicInformationHeader>
          <Typography category="subtitle" size="small" weight="bold">
            Basic Information
          </Typography>

          <Button type="tertiary" label="Edit" size="small" disableEffects="true" iconRight="edit"/>

        </BasicInformationHeader>

        <BasicInformationNotifications showNotificationDisabled={showNotificationDisabled}>
          <Alert
            title="User account is deactivated"
            description="This user was manually deactivated and currently cannot access the workspace. You can activate the account to restore access."
            severity="neutral"
            maxWidth="100%"
            closable={false}
            autoClose={false}
            position="relative"
            iconPosition="flex-start"
            noShadow={true}
            action={"Activate user"}
            actionButtonProps={{
              "type": "primary",
              "weight": "regular",
              "size": "small",
              "category": "body"
            }}
          />
        </BasicInformationNotifications>

        <BasicInformationNotificationsInvExpired showInvitationExpired={showInvitationExpired}>
          <Alert
            title="Invitation expired"
            description="This user did not accept the invitation within the allowed time. You can resend the invitation to let them join the workspace."
            severity="error"
            maxWidth="100%"
            closable={false}
            autoClose={false}
            position="relative"
            iconPosition="flex-start"
            noShadow={true}
            action={"Resend invitation"}
            actionButtonProps={{
              "type": "primary",
              "weight": "regular",
              "size": "small",
              "category": "body"
            }}
          />
        </BasicInformationNotificationsInvExpired>

        <BasicInformationNotificationsInvSent showInvitationSent={showInvitationSent}>
          <Alert
            title="Invitation sent"
            description="The invitation was sent successfully. This user needs to accept it to complete their account setup and gain access to the workspace."
            severity="warning"
            maxWidth="100%"
            closable={false}
            autoClose={false}
            position="relative"
            iconPosition="flex-start"
            noShadow={true}
            action={"Send reminder"}
            actionButtonProps={{
              "type": "primary",
              "weight": "regular",
              "size": "small",
              "category": "body"
            }}
          />
        </BasicInformationNotificationsInvSent>

        <BasicInformationContent>
          <BasicInformationContentAvatar>
          <Avatar
            alt={userData.fullName || "User avatar"}
            size="lg"
            src="https://placehold.co/32x32"
          />
          </BasicInformationContentAvatar>

          <BasicInformationContentData>
            <BasicInformationContentDataItem>
              <InfoItem>
                <Typography category="body" size="medium" weight="regular">
                  Full Name
                </Typography>
                <Typography category="body" size="medium" weight="regular" color="body">
                  {userData.fullName || "—"}
                </Typography>
              </InfoItem>
              <InfoItem>
                <Typography category="body" size="medium" weight="regular">
                  Email Address
                </Typography>
              
                <InfoItemRow gapSpacing={"8px"}>
                  <Icon name="email" size="small" />
                  <Typography category="body" size="medium" weight="regular" color="body">
                    {userData.email || "—"}
                  </Typography>
                </InfoItemRow>
              </InfoItem>
            </BasicInformationContentDataItem>
            <BasicInformationContentDataItem>
              <InfoItem>
                <Typography category="body" size="medium" weight="regular">
                  User Type
                </Typography>
                <InfoItemRow>
                  <Icon name="adminPanelSettings" size="small" />
                  <Typography category="body" size="medium" weight="regular" color="body">
                    {getUserTypeDisplay(userData.userType)}
                  </Typography>
                </InfoItemRow>
              </InfoItem>
              <InfoItem>
                <Typography category="body" size="medium" weight="regular">
                  State
                </Typography>
                <Typography category="body" size="medium" weight="regular" color="body">
                 <Tag color={getStateTagColor(userData.state)} size="small" text={getStateDisplay(userData.state)}/>
                </Typography>
              </InfoItem>
            </BasicInformationContentDataItem>
            <BasicInformationContentDataItem>
              <InfoItem>
                <Typography category="body" size="medium" weight="regular">
                  MFA
                </Typography>
                <Typography category="body" size="medium" weight="regular" color="body">
                  <Tag color="informative" size="small" text="Enabled"/>
                </Typography>
              </InfoItem>
              <InfoItem>
                <Typography category="body" size="medium" weight="regular">
                  Invited by
                </Typography>
                <InfoItemRow>
                  <Avatar alt={userData.createdByName || "Avatar"} size="sm" src="https://placehold.co/16x16"/>
                  <Typography category="body" size="medium" weight="regular" color="body">
                    {userData.createdByName || "—"}
                  </Typography>
                </InfoItemRow>
              </InfoItem>
            </BasicInformationContentDataItem>
          </BasicInformationContentData>
        </BasicInformationContent>
      </BasicInformation>

      <LoginInformation>
        <BasicInformationHeader>
          <Typography category="subtitle" size="small" weight="bold">
            Login Information
          </Typography>

        </BasicInformationHeader>

        <LoginInformationContent>
          <Card size="medium">
            <LoginInformationContentCardWrapper>
              <LoginInformationContentCardLeft>
                <Typography category="body" size="small" weight="regular">
                  Last Login
                </Typography>
                <Typography category="title" size="medium" weight="bold">
                  {userData.lastLogin ? formatLastLogin(userData.lastLogin) : "—"}
                </Typography>
              </LoginInformationContentCardLeft>
              <LoginInformationContentCardRight>
                <IconHighlight bgColor="Primary" icon="schedule" iconColor="Success" size="medium"/>
              </LoginInformationContentCardRight>
            </LoginInformationContentCardWrapper>
          </Card>
          <Card size="medium">
            <LoginInformationContentCardWrapper>
              <LoginInformationContentCardLeft>
                <Typography category="body" size="small" weight="regular">
                  Date created
                </Typography>
                <Typography category="title" size="medium" weight="bold">
                  {formatCreationDate(userData.createdAt)}
                </Typography>
              </LoginInformationContentCardLeft>
              <LoginInformationContentCardRight>
                <IconHighlight bgColor="Primary" icon="calendarToday" iconColor="Success" size="medium"/>
              </LoginInformationContentCardRight>
            </LoginInformationContentCardWrapper>
          </Card>
          <Card size="medium">
            <LoginInformationContentCardWrapper>
              <LoginInformationContentCardLeft>
                <Typography category="body" size="small" weight="regular">
                  Total Logins
                </Typography>
                <Typography category="title" size="medium" weight="bold">
                  {userData.totalLogins ?? "—"}
                </Typography>
              </LoginInformationContentCardLeft>
              <LoginInformationContentCardRight>
                <IconHighlight bgColor="Primary" icon="list" iconColor="Success" size="medium"/>
              </LoginInformationContentCardRight>
            </LoginInformationContentCardWrapper>
          </Card>
          

        </LoginInformationContent>
      </LoginInformation>

      <MultiFactor>
        <MultiFactorHeader>
          <Typography category="subtitle" size="small" weight="bold">
            Login Information
          </Typography>

          <Typography category="body" size="medium" weight="regular" color="body">
            This user has successfully completed email-based MFA. Secure access is active on this account.
          </Typography>
        </MultiFactorHeader>

        <MultiFactorState>
          <MultiFactorStateLeft>
            <IconHighlight bgColor="Primary" icon="email" iconColor="Success" size="medium"/>
          </MultiFactorStateLeft>
          <MultiFactorStateRight>
            <Typography category="body" size="medium" weight="regular">
               State
            </Typography>
            <Tag color="informative" size="small" text="Enabled"/>

          </MultiFactorStateRight>
        </MultiFactorState>
      </MultiFactor>

    </OverviewContainer>
  );
};

export default Overview;

